import { Field } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Grid, makeStyles, Tooltip, Typography, InputAdornment } from '@material-ui/core';
import FormikTextField from '../../../../../FormikFields/FormikTextField';
import cx from 'classnames';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  rowContainer: {
    marginBottom: theme.spacing(2),
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 1),
  },
  toggleLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginRight: theme.spacing(1),
  },
  languageContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  languageField: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  textArea: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'rgba(248, 250, 252, 0.8)',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        '& fieldset': {
          borderColor: theme.colors.lightBlue,
          borderWidth: 2,
        },
      },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  disabledOverlay: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(248, 250, 252, 0.7)',
      borderRadius: 8,
      zIndex: 1,
      pointerEvents: 'none',
    },
  },
  variableHighlight: {
    '& .MuiInputBase-input': {
      fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  previewContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(248, 250, 252, 0.6)',
    borderRadius: 6,
    border: '1px solid rgba(0, 0, 0, 0.08)',
  },
  previewLabel: {
    fontSize: '0.625rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
  },
  previewText: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    '& .variable': {
      color: theme.colors.lightBlue,
      fontWeight: 600,
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      padding: '1px 4px',
      borderRadius: 3,
      fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
    },
  },
}));

const VariableHighlightedText = ({ text }) => {
  if (!text) return null;
  
  const parts = text.split(/(\{[^}]+\})/);
  
  return (
    <span>
      {parts.map((part, index) => {
        if (part.match(/^\{[^}]+\}$/)) {
          return (
            <span key={index} className="variable">
              {part}
            </span>
          );
        }
        return part;
      })}
    </span>
  );
};

const CustomEmailSectionRow = ({
                                 isDistributionSponsor,
                                 rowName,
                                 firstFieldName,
                                 secondFieldName,
                                 idDefaultName,
                                 backendErrors,
                                 defaultFirstFieldText,
                                 defaultSecondFieldText,
                               }) => {

  const defaultTextInputs = useMemo(
    () => (
      <SectionRowLayout
        isDistributionSponsor={isDistributionSponsor}
        rowName={rowName}
        firstFieldName={'loading'}
        secondFieldName={'loading'}
        idDefaultName={idDefaultName}
        form={{
          values: {},
        }}
        backendErrors={{}}
        isDisabledFields={true}
        changeIsCheck={() => {
        }}
      />
    ), [defaultFirstFieldText, defaultSecondFieldText]);

  if (!defaultFirstFieldText || !defaultSecondFieldText) {
    return defaultTextInputs;
  }

  return (
    <Field name={'customEmailSection'}>
      {({ form }) => {
        return <SectionRow
          isDistributionSponsor={isDistributionSponsor}
          rowName={rowName}
          firstFieldName={firstFieldName}
          secondFieldName={secondFieldName}
          idDefaultName={idDefaultName}
          form={form}
          backendErrors={backendErrors}
          defaultFirstFieldText={defaultFirstFieldText}
          defaultSecondFieldText={defaultSecondFieldText}
        />;
      }}
    </Field>
  );
};

const SectionRow = ({
                      isDistributionSponsor,
                      rowName,
                      firstFieldName,
                      secondFieldName,
                      idDefaultName,
                      backendErrors,
                      form,
                      defaultFirstFieldText,
                      defaultSecondFieldText,
                    }) => {
  const [temporaryValues, setTemporaryValues] = useState({
    [firstFieldName]: undefined,
    [secondFieldName]: undefined,
  });
  const [shouldUpdateValueAfterSaving, setShouldUpdateValueAfterSaving] = useState('init');

  const changeIsCheck = (checked) => {
    if (checked) {
      form.setFieldValue(firstFieldName, temporaryValues[firstFieldName] ?? defaultFirstFieldText);
      form.setFieldValue(secondFieldName, temporaryValues[secondFieldName] ?? defaultSecondFieldText);
      form.setFieldValue(idDefaultName, false);
    } else {
      form.setFieldValue(firstFieldName, defaultFirstFieldText);
      form.setFieldValue(secondFieldName, defaultSecondFieldText);
      form.setFieldValue(idDefaultName, true);
    }
  };

  useEffect(() => {
    setTemporaryValues({
      [firstFieldName]: form.values[firstFieldName],
      [secondFieldName]: form.values[secondFieldName],
    });
  }, []);

  useEffect(() => {
    if (shouldUpdateValueAfterSaving === 'saved') {
      if (!form.values[idDefaultName]) {
        setTemporaryValues({
          [firstFieldName]: form.values[firstFieldName],
          [secondFieldName]: form.values[secondFieldName],
        });
      } else {
        form.setFieldValue(firstFieldName, defaultFirstFieldText);
        form.setFieldValue(secondFieldName, defaultSecondFieldText);
      }
    }
  }, [shouldUpdateValueAfterSaving]);

  useEffect(() => {
    if (!form.values[idDefaultName]) {
      setTemporaryValues({
        [firstFieldName]: form.values[firstFieldName],
        [secondFieldName]: form.values[secondFieldName],
      });
    }
  }, [form.values[firstFieldName], form.values[secondFieldName]]);

  useEffect(() => {
    if (form.isSubmitting && (shouldUpdateValueAfterSaving === 'init' || shouldUpdateValueAfterSaving === 'saved')) {
      setShouldUpdateValueAfterSaving('saving');
    } else if (!form.isSubmitting && shouldUpdateValueAfterSaving === 'saving') {
      setShouldUpdateValueAfterSaving('saved');
    }
  }, [form.isSubmitting]);


  return (
    <SectionRowLayout
      isDistributionSponsor={isDistributionSponsor}
      rowName={rowName}
      firstFieldName={firstFieldName}
      secondFieldName={secondFieldName}
      idDefaultName={idDefaultName}
      form={form}
      backendErrors={backendErrors}
      isDisabledFields={false}
      changeIsCheck={changeIsCheck}
    />
  );
};

const SectionRowLayout = ({
                            isDistributionSponsor,
                            rowName,
                            firstFieldName,
                            secondFieldName,
                            idDefaultName,
                            backendErrors,
                            form,
                            isDisabledFields,
                            changeIsCheck,
                          }) => {
  const classes = useStyles();
  const { en: { forms, tooltips } } = useSelector((state) => state.i18n);
  const parsedErrorFirstKey = firstFieldName.startsWith('email') ? 'emailConfiguration' : 'smsConfiguration';
  const parsedErrorSecondKey = firstFieldName.replace('email', '').replace('sms', '').replace('English', '').replace('Spanish', '');
  const errorName = `${parsedErrorFirstKey}.${parsedErrorSecondKey}`;
  const firstFieldNameError = backendErrors && (backendErrors[`${errorName}[0].text`] || backendErrors[`${errorName}[0]`] || backendErrors[`${errorName}[0].local`]);
  const secondFieldNameError = backendErrors && (backendErrors[`${errorName}[1].text`] || backendErrors[`${errorName}[1]`] || backendErrors[`${errorName}[1].local`]);

  return (
    <div className={classes.rowContainer}>
      <div className={classes.toggleContainer}>
        <Typography className={classes.toggleLabel}>
          Override Default
        </Typography>
        <Tooltip
          disableFocusListener={!isDistributionSponsor}
          disableHoverListener={!isDistributionSponsor}
          disableTouchListener={!isDistributionSponsor}
          title={tooltips.availableOnlyForDS}
        >
          <Checkbox
            name={'isFieldsDefault'}
            checked={!form.values[idDefaultName]}
            color='primary'
            onChange={(e, checked) => {
              changeIsCheck(checked);
            }}
            disabled={isDisabledFields || !isDistributionSponsor}
          />
        </Tooltip>
      </div>
      
      <div className={classes.languageContainer}>
        <div className={classes.languageField}>
          <Typography className={classes.fieldLabel}>
            English
          </Typography>
          <FormikTextField
            type='text'
            name={firstFieldName}
            placeholder='Enter English message...'
            rowsMax={4}
            rows={4}
            multiline
            variant='outlined'
            disabled={isDisabledFields || (form.values[idDefaultName] && isDistributionSponsor)}
            backendError={firstFieldNameError}
            className={cx(classes.textArea, classes.variableHighlight, {
              [classes.disabledOverlay]: form.values[idDefaultName] && isDistributionSponsor
            })}
          />
          {form.values[firstFieldName] && (
            <div className={classes.previewContainer}>
              <Typography className={classes.previewLabel}>
                Preview
              </Typography>
              <div className={classes.previewText}>
                <VariableHighlightedText text={form.values[firstFieldName]} />
              </div>
            </div>
          )}
        </div>
        
        <div className={classes.languageField}>
          <Typography className={classes.fieldLabel}>
            Spanish
          </Typography>
          <FormikTextField
            type='text'
            name={secondFieldName}
            placeholder='Enter Spanish message...'
            rowsMax={4}
            rows={4}
            multiline
            variant='outlined'
            disabled={isDisabledFields || (form.values[idDefaultName] && isDistributionSponsor)}
            backendError={secondFieldNameError}
            className={cx(classes.textArea, classes.variableHighlight, {
              [classes.disabledOverlay]: form.values[idDefaultName] && isDistributionSponsor
            })}
          />
          {form.values[secondFieldName] && (
            <div className={classes.previewContainer}>
              <Typography className={classes.previewLabel}>
                Preview
              </Typography>
              <div className={classes.previewText}>
                <VariableHighlightedText text={form.values[secondFieldName]} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomEmailSectionRow;
